import type {
  SupplierOnboardingCompleteRequestType,
  SupplierOnboardingCompleteResponseType,
  SupplierOnboardingGetResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { getBusinessQueryKey } from "../business/useBusiness";
import { trpc } from "../trpc";

const supplierOnboardingQueryKey = "supplier-onboarding";

const getSupplierOnboardingQueryKey = ({
  userId,
  businessId,
}: {
  userId: string | undefined;
  businessId: number | undefined;
}) => [userId, businessId, supplierOnboardingQueryKey] as const;

export const useSupplierOnboarding = ({ enabled = true } = {}) => {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();

  return useQuery<SupplierOnboardingGetResponseType>({
    queryKey: getSupplierOnboardingQueryKey({
      userId: user?.uid,
      businessId: business?.id,
    }),
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "supplier-onboarding",
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as SupplierOnboardingGetResponseType,
      ),
    enabled: Boolean(enabled && user && business?.Supplier),
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export const useSupplierOnboardingComplete = () => {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const queryClient = useQueryClient();
  const trpcUtils = trpc.useUtils();

  return useMutation({
    mutationFn: (data: SupplierOnboardingCompleteRequestType) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: "supplier-onboarding/complete",
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as SupplierOnboardingCompleteResponseType,
      ),
    onSuccess: async (response) => {
      if (response.failedInvitationEmails.length > 0) return;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getBusinessQueryKey({
            userId: user?.uid,
            businessId: business?.id,
          }),
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: getSupplierOnboardingQueryKey({
            userId: user?.uid,
            businessId: business?.id,
          }),
          exact: true,
        }),
        trpcUtils.featureFlag.list.invalidate(),
      ]);
    },
  });
};
